import { parseReplacementWithPauses } from './pause-tags';

/** The minimum a word needs to expose to be matched: its text. */
export type MatchableWord = { word: string };

/**
 * The minimum an interleaved (word + pause) token needs to expose to be matched: its text and
 * whether it is a pause. Pauses are matched presence-only (their length is ignored), like
 * edit_ai_voiceover, so `InterleavedWord` satisfies this directly.
 */
export type MatchableToken = { word: string; isPause?: boolean };

export type WordSequenceMatch = { startIndex: number; endIndex: number };

/**
 * Format / invisible characters that carry no word identity and must be dropped before comparison:
 * tatweel (Arabic elongation, U+0640), zero-width space/non-joiner/joiner (U+200B–200D), BOM
 * (U+FEFF), LRM/RLM/ALM and the bidi embeddings/overrides/isolates (U+200E–200F, U+061C,
 * U+202A–202E, U+2066–2069). The agent (or the ASR) can insert any of these without changing the
 * spoken word, so leaving them in breaks exact-string comparison for Arabic and other scripts.
 */
const FORMAT_NOISE_REGEX = /[ـ​-‍﻿‎‏؜‪-‮⁦-⁩]/gu;

/**
 * Regex identifying scripts whose ASR tokens are not space-delimited, so a request's word grouping
 * cannot be assumed to match the transcript's token boundaries. Used to gate the character-run
 * fallback so space-delimited scripts (Latin, Cyrillic, Arabic, Devanagari, …) stay a strict no-op.
 */
const SPACELESS_SCRIPT_REGEX =
    /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}\p{Script=Khmer}\p{Script=Lao}\p{Script=Myanmar}]/u;

/**
 * Normalize a transcript token for tolerant matching: apply NFC (so composed and decomposed Unicode
 * forms compare equal), lowercase, strip format/invisible characters (tatweel, ZWJ/ZWNJ, bidi
 * marks — see {@link FORMAT_NOISE_REGEX}), then strip leading/trailing punctuation and quotes, so
 * "Editorial.", "editorial" and "“editorial”" all compare equal. Internal characters are kept (so
 * "don't" and "state-of-the-art" survive intact). A token that is nothing but punctuation/format
 * noise (e.g. a lone ".", or a bidi mark) normalizes to an empty string. No-op on plain ASCII.
 */
export function normalizeTranscriptToken(token: string): string {
    return token
        .normalize('NFC')
        .toLowerCase()
        .replace(FORMAT_NOISE_REGEX, '')
        .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
}

/**
 * Whether a stretch of concatenated, normalized transcript content is in a spaceless script (CJK,
 * Thai, Khmer, Lao, Myanmar). Only such transcripts enable the character-run fallback below.
 */
export function isSpacelessContent(concat: string): boolean {
    return SPACELESS_SCRIPT_REGEX.test(concat);
}

/**
 * Whether these normalized transcript words need the character-run fallback: a spaceless script, or
 * a word holding several (a word-for-word machine translation of "same" gives "такой же"), which a
 * whitespace-split request can never match token-for-token.
 */
export function needsCharRunFallback(normalizedWords: string[]): boolean {
    return (
        isSpacelessContent(normalizedWords.join('')) ||
        normalizedWords.some((word) => /\s/u.test(word))
    );
}

/** Whitespace is dropped so "такой же" compares equal to the request's "такой" + "же". */
function charRunChars(token: string): string {
    return normalizeTranscriptToken(token).replace(/\s+/gu, '');
}

/** A pattern segment for the pause-aware character-run matcher: a run of word characters, or a pause. */
export type PatternSegment = { kind: 'wordRun'; chars: string } | { kind: 'pause' };

/**
 * Find EVERY run of consecutive transcript tokens whose concatenated characters exactly equal
 * `requestChars`. `tokenChars[i]` is token i's already-normalized characters; a returned run's
 * indices are indices into `tokenChars`. A run must begin and end on a token boundary, so a request
 * that covers only PART of a transcript token (e.g. transcript "定制", request "定") correctly
 * returns nothing — the ASR gives no sub-token timing to split on.
 *
 * This dissolves segmentation/spacing differences (the agent regrouping spaceless-script characters
 * into different words than the ASR emitted) while keeping the transcript's own token boundaries as
 * the only cut points, so each returned index still maps to an exact per-word timing.
 */
export function findCharRunMatches(
    tokenChars: string[],
    requestChars: string,
): WordSequenceMatch[] {
    const matches: WordSequenceMatch[] = [];
    if (requestChars === '') {
        return matches;
    }
    for (let i = 0; i < tokenChars.length; i++) {
        let concat = '';
        for (let j = i; j < tokenChars.length; j++) {
            concat += tokenChars[j];
            if (concat.length > requestChars.length) {
                break;
            }
            if (concat === requestChars) {
                matches.push({ startIndex: i, endIndex: j });
                break;
            }
        }
    }
    return matches;
}

/**
 * Collapse a requested word/pause pattern into character-run segments: consecutive words concatenate
 * into one `wordRun` (their normalized characters joined), and each pause becomes its own `pause`
 * segment. This is the request side of the pause-aware character-run fallback.
 */
export function patternSegmentsFromPattern(pattern: PatternToken[]): PatternSegment[] {
    const segments: PatternSegment[] = [];
    let chars = '';
    for (const token of pattern) {
        if (token.kind === 'pause') {
            if (chars !== '') {
                segments.push({ kind: 'wordRun', chars });
                chars = '';
            }
            segments.push({ kind: 'pause' });
        } else {
            chars += charRunChars(token.text);
        }
    }
    if (chars !== '') {
        segments.push({ kind: 'wordRun', chars });
    }
    return segments;
}

/**
 * Try to match `segments` against `tokens` starting exactly at `start`, returning the index of the
 * last consumed token or null. A `wordRun` consumes consecutive word tokens until their concatenated
 * characters equal the run; a pause token encountered mid-run terminates it before it completes → no
 * match (preserving strict pause semantics — an in-span pause omitted from the request is a loud
 * miss). A `pause` segment consumes exactly one pause token.
 */
function tryMatchInterleavedCharRunAt(
    tokens: MatchableToken[],
    segments: PatternSegment[],
    start: number,
): number | null {
    let pos = start;
    for (const segment of segments) {
        if (segment.kind === 'pause') {
            if (pos >= tokens.length || !tokens[pos].isPause) {
                return null;
            }
            pos++;
            continue;
        }
        let concat = '';
        let matched = false;
        while (pos < tokens.length) {
            const token = tokens[pos];
            if (token.isPause) {
                return null;
            }
            concat += charRunChars(token.word);
            pos++;
            if (concat.length > segment.chars.length) {
                return null;
            }
            if (concat === segment.chars) {
                matched = true;
                break;
            }
        }
        if (!matched) {
            return null;
        }
    }
    return pos - 1;
}

/**
 * Pause-aware character-run matcher: find every run of `tokens` matching `segments`, where each
 * `wordRun` matches consecutive word tokens by concatenated characters (dissolving grouping
 * differences) and each `pause` matches a present pause token. Returned indices are indices into
 * `tokens`. The counterpart of {@link findCharRunMatches} for the interleaved word+pause stream.
 */
export function matchInterleavedCharRuns(
    tokens: MatchableToken[],
    segments: PatternSegment[],
): WordSequenceMatch[] {
    const matches: WordSequenceMatch[] = [];
    if (segments.length === 0) {
        return matches;
    }
    for (let start = 0; start < tokens.length; start++) {
        const endIndex = tryMatchInterleavedCharRunAt(tokens, segments, start);
        if (endIndex !== null) {
            matches.push({ startIndex: start, endIndex });
        }
    }
    return matches;
}

/**
 * Find EVERY place a sequence of requested words occurs in a transcript, tolerant of
 * punctuation, quotes, case, and whether the caller passed the phrase as a single string or as
 * separate tokens. Punctuation-only tokens on either side are ignored. Each match is the
 * ORIGINAL transcript indices of its first and last matched words.
 *
 * Callers that need a single result use findWordSequenceIndices; callers that must detect
 * ambiguity (e.g. edit_ai_voiceover rejecting a phrase that appears more than once) use this.
 *
 * `allowSpacelessFallback` (default true) enables the spaceless-script character-run fallback. A
 * caller that maps the match back onto its OWN word list by index — where a run of transcript
 * tokens regrouped differently from the request would break that index alignment — must pass
 * `false` to stay strict (see setPausesInRange).
 */
export function findAllWordSequenceMatches(
    transcriptWords: readonly MatchableWord[],
    requestedWords: string[],
    { allowSpacelessFallback = true }: { allowSpacelessFallback?: boolean } = {},
): WordSequenceMatch[] {
    // Re-tokenize the request (a phrase passed as one element becomes its words), normalize,
    // and drop punctuation-only tokens.
    const normalizedRequest = requestedWords
        .flatMap((word) => word.split(/\s+/))
        .map(normalizeTranscriptToken)
        .filter((token) => token !== '');
    if (normalizedRequest.length === 0) {
        return [];
    }

    // Normalized transcript tokens, dropping punctuation-only tokens but keeping a map back to
    // their original indices.
    const normalizedTranscript = transcriptWords
        .map((word, index) => ({ norm: normalizeTranscriptToken(word.word), index }))
        .filter((token) => token.norm !== '');

    const matches: WordSequenceMatch[] = [];
    for (let i = 0; i <= normalizedTranscript.length - normalizedRequest.length; i++) {
        let match = true;
        for (let j = 0; j < normalizedRequest.length; j++) {
            if (normalizedTranscript[i + j].norm !== normalizedRequest[j]) {
                match = false;
                break;
            }
        }
        if (match) {
            matches.push({
                startIndex: normalizedTranscript[i].index,
                endIndex: normalizedTranscript[i + normalizedRequest.length - 1].index,
            });
        }
    }
    if (matches.length > 0 || !allowSpacelessFallback) {
        return matches;
    }

    // Character-run fallback: the transcript's token grouping and the agent's may differ while the
    // characters are identical (spaceless scripts, multi-word tokens), so strict token equality
    // misses. Only runs after strict finds nothing, and ordinary transcripts never enter it.
    if (!needsCharRunFallback(normalizedTranscript.map((token) => token.norm))) {
        return matches;
    }
    const tokenChars = normalizedTranscript.map((token) => charRunChars(token.norm));
    return findCharRunMatches(tokenChars, normalizedRequest.join('')).map((run) => ({
        startIndex: normalizedTranscript[run.startIndex].index,
        endIndex: normalizedTranscript[run.endIndex].index,
    }));
}

/**
 * Find where a sequence of requested words first occurs in a transcript, or null if the
 * normalized sequence does not appear consecutively.
 *
 * Shared by the word-driven transcript tools (split_scene_group, add_voiceover_scene) so an agent that
 * spells punctuation slightly differently than the transcript stored it ("editorial." vs
 * "editorial", a stray "." token, curly vs straight quotes) still lands the right words.
 */
export function findWordSequenceIndices(
    transcriptWords: readonly MatchableWord[],
    requestedWords: string[],
): WordSequenceMatch | null {
    return findAllWordSequenceMatches(transcriptWords, requestedWords)[0] ?? null;
}

/** A token in a requested phrase's pattern. Pauses are presence-only (their N is ignored). */
type PatternToken = { kind: 'word'; text: string } | { kind: 'pause' };

/**
 * Parse a requested phrase (which may contain `<pause seconds="N"/>` tags) into an ordered pattern
 * of word and pause tokens. Punctuation-only words are dropped (like the word-only matcher), so
 * normalization tolerance is identical everywhere; each pause tag becomes one presence-only pause
 * token positioned among the words exactly where it was written.
 */
function buildRequestedPattern(requestedWords: string[]): PatternToken[] {
    const { words, pauses } = parseReplacementWithPauses(requestedWords.join(' '));
    const tokens: PatternToken[] = [];
    for (let i = 0; i <= words.length; i++) {
        for (const pause of pauses) {
            if (pause.afterWordIndex === i) {
                tokens.push({ kind: 'pause' });
            }
        }
        if (i < words.length && normalizeTranscriptToken(words[i]) !== '') {
            tokens.push({ kind: 'word', text: words[i] });
        }
    }
    return tokens;
}

function interleavedTokenMatches(token: MatchableToken, pattern: PatternToken): boolean {
    if (pattern.kind === 'pause') {
        // Presence-only: a pattern pause matches any pause token, ignoring its length.
        return !!token.isPause;
    }
    if (token.isPause) {
        return false;
    }
    return normalizeTranscriptToken(token.word) === normalizeTranscriptToken(pattern.text);
}

/**
 * Find EVERY place a requested phrase occurs in the canonical interleaved (word + pause) token
 * list as a strict, literal run — the pause-aware counterpart of findAllWordSequenceMatches.
 * Words match words with the usual punctuation/case tolerance (via normalizeTranscriptToken);
 * a `<pause>` in the request matches a pause token, presence-only.
 *
 * The match is STRICT over pauses, mirroring edit_ai_voiceover: a pause token that falls inside the
 * requested span but is left out of the request breaks the consecutive run, so it simply is not
 * found (a loud failure) rather than being silently swallowed. Punctuation-only transcript tokens
 * stay transparent (dropped on both sides), so a request with no pauses over a pause-less span
 * behaves exactly as the word-only matcher does. Returned indices are ORIGINAL indices into
 * `tokens`, so callers can read each matched token's startTime/endTime.
 */
export function findAllInterleavedMatches(
    tokens: MatchableToken[],
    requestedWords: string[],
): WordSequenceMatch[] {
    const pattern = buildRequestedPattern(requestedWords);
    if (pattern.length === 0) {
        return [];
    }

    // Keep pauses and non-punctuation words, remembering each kept token's original index.
    const kept = tokens
        .map((token, index) => ({ token, index }))
        .filter(({ token }) => token.isPause || normalizeTranscriptToken(token.word) !== '');

    const matches: WordSequenceMatch[] = [];
    for (let i = 0; i <= kept.length - pattern.length; i++) {
        let matched = true;
        for (let j = 0; j < pattern.length; j++) {
            if (!interleavedTokenMatches(kept[i + j].token, pattern[j])) {
                matched = false;
                break;
            }
        }
        if (matched) {
            matches.push({
                startIndex: kept[i].index,
                endIndex: kept[i + pattern.length - 1].index,
            });
        }
    }
    if (matches.length > 0) {
        return matches;
    }

    // Character-run fallback — see findAllWordSequenceMatches. Match runs of word tokens by
    // concatenated characters (pause-aware) so a regrouped request still lands on the right span.
    const normalizedWords = kept
        .filter(({ token }) => !token.isPause)
        .map(({ token }) => normalizeTranscriptToken(token.word));
    if (!needsCharRunFallback(normalizedWords)) {
        return matches;
    }
    const segments = patternSegmentsFromPattern(pattern);
    return matchInterleavedCharRuns(
        kept.map(({ token }) => token),
        segments,
    ).map((run) => ({
        startIndex: kept[run.startIndex].index,
        endIndex: kept[run.endIndex].index,
    }));
}

/**
 * Find where a requested phrase (words, optionally with `<pause>` tags) first occurs in the
 * canonical interleaved token list, or null if it does not appear consecutively. The pause-aware
 * counterpart of findWordSequenceIndices, shared by the word-driven placement tools so they match
 * against exactly the transcript the agent reads.
 */
export function findInterleavedSequenceMatch(
    tokens: MatchableToken[],
    requestedWords: string[],
): WordSequenceMatch | null {
    return findAllInterleavedMatches(tokens, requestedWords)[0] ?? null;
}
